import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  api,
  fmtKwh,
  isoDato,
  type Charger,
  type ChargerState,
  type Me,
  type SpotPrice,
} from '../lib/api.ts';
import PriceChart from '../components/PriceChart.tsx';
import { Badge, knappPrimær, knappSekundær } from '../components/ui.tsx';

export default function Dashboard({ me }: { me: Me }) {
  const qc = useQueryClient();
  const chargersQuery = useQuery<Charger[]>({
    queryKey: ['chargers'],
    queryFn: () => api.get('/chargers'),
    refetchInterval: 120_000,
  });
  const areasQuery = useQuery<string[]>({
    queryKey: ['areas'],
    queryFn: () => api.get('/prices/areas'),
  });

  const [valgtOmrade, setValgtOmrade] = useState<string | null>(null);
  const areas = areasQuery.data ?? [];
  const area = valgtOmrade ?? areas[0] ?? null;

  const idag = isoDato(new Date());
  const imorgen = isoDato(new Date(Date.now() + 86_400_000));
  const priserIdag = useQuery<SpotPrice[]>({
    queryKey: ['prices', area, idag],
    queryFn: () => api.get(`/prices?area=${area}&date=${idag}`),
    enabled: !!area,
  });
  const priserImorgen = useQuery<SpotPrice[]>({
    queryKey: ['prices', area, imorgen],
    queryFn: () => api.get(`/prices?area=${area}&date=${imorgen}`),
    enabled: !!area,
    refetchInterval: 15 * 60_000,
  });

  // Live status via SSE
  useEffect(() => {
    const es = new EventSource('/api/v1/chargers/stream');
    es.addEventListener('state', (ev) => {
      const { chargerId, state } = JSON.parse((ev as MessageEvent).data) as {
        chargerId: string;
        state: ChargerState;
      };
      qc.setQueryData<Charger[]>(['chargers'], (prev) =>
        prev?.map((c) => (c.id === chargerId ? { ...c, sistState: state } : c)),
      );
    });
    return () => es.close();
  }, [qc]);

  const chargers = (chargersQuery.data ?? []).filter((c) => c.aktiv);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="mb-3 text-xl font-bold text-slate-800">Dashbord</h1>
        {chargersQuery.isLoading ? (
          <p className="text-slate-500">Laster ladere …</p>
        ) : chargers.length === 0 ? (
          <div className="rounded-xl bg-white p-6 text-sm text-slate-500 ring-1 ring-slate-200">
            Ingen ladere ennå.{' '}
            {(me.rolle === 'admin' || me.rolle === 'sysadmin') && (
              <>
                Legg til en leverandørkonto under{' '}
                <Link to="/register" className="text-blue-600 underline">
                  Register
                </Link>{' '}
                for å hente inn laderne dine.
              </>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {chargers.map((c) => (
              <ChargerCard key={c.id} charger={c} me={me} />
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          {areas.length > 1 &&
            areas.map((a) => (
              <button
                key={a}
                onClick={() => setValgtOmrade(a)}
                className={`rounded-lg px-3 py-1 text-sm ${
                  a === area ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'
                }`}
              >
                {a}
              </button>
            ))}
        </div>
        {area && (
          <PriceChart idag={priserIdag.data ?? []} imorgen={priserImorgen.data ?? []} area={area} />
        )}
      </div>
    </div>
  );
}

function ChargerCard({ charger, me }: { charger: Charger; me: Me }) {
  const qc = useQueryClient();
  const [feil, setFeil] = useState<string | null>(null);
  const state = charger.sistState;
  const kanStyre = me.rolle === 'admin' || me.rolle === 'sysadmin' || charger.personId === me.id;

  const kommando = useMutation({
    mutationFn: (hva: 'start' | 'stop') => api.post(`/chargers/${charger.id}/${hva}`),
    onError: (err) => setFeil(err instanceof Error ? err.message : 'Kommandoen feilet'),
    onSuccess: () => {
      setFeil(null);
      // Status oppdateres via SSE etter noen sekunder
      setTimeout(() => qc.invalidateQueries({ queryKey: ['chargers'] }), 6_000);
    },
  });

  return (
    <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
      <div className="flex items-start justify-between gap-2">
        <div>
          <Link to={`/ladere/${charger.id}`} className="font-semibold text-slate-800 hover:text-blue-700">
            {charger.navn}
          </Link>
          <p className="text-xs text-slate-400">
            {charger.anleggNavn} · {charger.eierNavn ?? 'uten eier'}
          </p>
        </div>
        <Badge state={state} />
      </div>

      <div className="mt-3 flex items-end justify-between">
        <div className="text-sm text-slate-600">
          {state?.modus === 'LADER' && state.effektKw !== null && (
            <p className="text-lg font-bold text-green-700">{state.effektKw.toFixed(1)} kW</p>
          )}
          {state?.oktKwh != null && state.modus !== 'FRAKOBLET' && (
            <p>Økt: {fmtKwh(state.oktKwh)}</p>
          )}
        </div>
        {kanStyre && state?.online && (
          <div className="flex gap-2">
            {state.modus === 'LADER' ? (
              <button
                onClick={() => kommando.mutate('stop')}
                disabled={kommando.isPending}
                className={knappSekundær}
              >
                Stopp
              </button>
            ) : state.modus !== 'FRAKOBLET' ? (
              <button
                onClick={() => kommando.mutate('start')}
                disabled={kommando.isPending}
                className={knappPrimær}
              >
                Lad nå
              </button>
            ) : null}
          </div>
        )}
      </div>
      {feil && <p className="mt-2 text-xs text-red-600">{feil}</p>}
    </div>
  );
}
